import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { formatPartMasterOptionLabel } from '../../../utils/partMasterLabel';

const MasterDataDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  currentType,
  effectiveType,
  itemToDelete,
  deleteDependencies,
  deleteLoading,
  manufacturerSubTab
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {deleteDependencies?.hasDependencies
          ? `Cannot Delete ${(currentType.key === 'manufacturers' && manufacturerSubTab === 3) ? 'Model Variant' : currentType.key === 'assembly-types' ? 'Assembly Type' : currentType.key === 'assembly-type-configurations' ? 'Assembly Type Configuration' : effectiveType.label.slice(0, -1)}`
          : `Permanently Delete ${(currentType.key === 'manufacturers' && manufacturerSubTab === 3) ? 'Model Variant' : currentType.key === 'assembly-types' ? 'Assembly Type' : currentType.key === 'assembly-type-configurations' ? 'Assembly Type Configuration' : effectiveType.label.slice(0, -1)}`}
      </DialogTitle>
      <DialogContent>
        {deleteLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>Checking dependencies...</Typography>
          </Box>
        ) : deleteDependencies?.hasDependencies ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              This {(currentType.key === 'manufacturers' && manufacturerSubTab === 3) ? 'model variant' : currentType.key === 'assembly-types' ? 'assembly type' : currentType.key === 'assembly-type-configurations' ? 'assembly type configuration' : effectiveType.label.slice(0, -1).toLowerCase()} cannot be deleted because it has dependencies. Please remove these dependencies first.
            </Alert>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
              Dependencies:
            </Typography>
            {deleteDependencies.dependencies.map((dep, index) => (
              <Box key={index} sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {dep.type} ({dep.count})
                </Typography>
                <List dense sx={{ pl: 2 }}>
                  {dep.items.slice(0, 10).map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Warning fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          dep.type === 'Model Variants' 
                            ? `${item.name}${item.manufacturer ? ` (${item.manufacturer})` : ''}${item.type ? ` - ${item.type}` : ''}`
                            : dep.type === 'Assemblies'
                            ? `Serial: ${item.serialNumber}, Position: ${item.position || 'N/A'}, Aircraft: ${item.aircraft}`
                            : dep.type === 'Parts'
                            ? (formatPartMasterOptionLabel(item) || 'N/A')
                            : dep.type === 'Components'
                            ? `Serial: ${item.serialNumber || 'N/A'}, Part: ${item.partNumber || 'N/A'}, Assembly: ${item.assembly || 'N/A'}`
                            : dep.type === 'Assembly Type Configurations'
                            ? `Configuration for: ${item.name || 'Unknown'}`
                          : dep.type === 'Manufacturers'
                            ? `Category: ${item.category || 'N/A'}`
                          : dep.type === 'Models'
                            ? `${item.name}${item.type ? ` (${item.type})` : ''}`
                            : JSON.stringify(item)
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                  {dep.count > 10 && (
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`... and ${dep.count - 10} more`}
                        primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to permanently delete the {(currentType.key === 'manufacturers' && manufacturerSubTab === 3) ? 'model variant' : currentType.key === 'assembly-types' ? 'assembly type' : currentType.key === 'assembly-type-configurations' ? 'assembly type configuration' : effectiveType.label.slice(0, -1).toLowerCase()} <strong>"{itemToDelete?.name || itemToDelete?.category || (itemToDelete?.assemblyType?.name ? `for ${itemToDelete.assemblyType.name}` : '')}"</strong>.
              This action cannot be undone.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This will permanently remove the {(currentType.key === 'manufacturers' && manufacturerSubTab === 3) ? 'model variant' : currentType.key === 'assembly-types' ? 'assembly type' : currentType.key === 'assembly-type-configurations' ? 'assembly type configuration' : effectiveType.label.slice(0, -1).toLowerCase()} from the system. Make sure you have backed up any important data.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {deleteDependencies?.hasDependencies ? 'Close' : 'Cancel'}
        </Button>
        {!deleteDependencies?.hasDependencies && (
          <Button 
            onClick={onConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MasterDataDeleteDialog;
